
import { useState } from "react";
import "./Pages.css";

const Contact = () => {
  // ==========================================
  // FORM STATE
  // ==========================================
  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    email: "",
    service: "",
    message: "",
  });

  // ==========================================
  // VALIDATION ERRORS
  // ==========================================
  const [errors, setErrors] = useState({});

  // ==========================================
  // SUBMIT STATE
  // ==========================================
  const [loading, setLoading] = useState(false);

  // ==========================================
  // SUCCESS / ERROR MESSAGE
  // ==========================================
  const [apiMessage, setApiMessage] = useState({
    type: "",
    message: "",
  });

  // ==========================================
  // HANDLE INPUT CHANGE
  // ==========================================
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Remove validation error when user starts correcting
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));

    // Remove API message
    setApiMessage({
      type: "",
      message: "",
    });
  };

  // ==========================================
  // VALIDATE FORM
  // ==========================================
  const validateForm = () => {
    const newErrors = {};

    // ------------------------------------------
    // NAME
    // ------------------------------------------
    const name = formData.name.trim();

    if (!name) {
      newErrors.name = "Please enter your name.";
    } else if (name.length < 2) {
      newErrors.name = "Name must be at least 2 characters.";
    } else if (!/^[a-zA-Z\s]+$/.test(name)) {
      newErrors.name = "Name can contain only letters and spaces.";
    }

    // ------------------------------------------
    // MOBILE
    // ------------------------------------------
    const mobile = formData.mobile.trim();

    if (!mobile) {
      newErrors.mobile = "Please enter your mobile number.";
    } else if (!/^[6-9]\d{9}$/.test(mobile)) {
      newErrors.mobile =
        "Please enter a valid 10-digit Indian mobile number.";
    }

    // ------------------------------------------
    // EMAIL
    // ------------------------------------------
    const email = formData.email.trim();

    if (!email) {
      newErrors.email = "Please enter your email address.";
    } else if (
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
    ) {
      newErrors.email = "Please enter a valid email address.";
    }

    // ------------------------------------------
    // SERVICE
    // ------------------------------------------
    if (!formData.service) {
      newErrors.service = "Please select a service.";
    }

    // ------------------------------------------
    // MESSAGE
    // ------------------------------------------
    const message = formData.message.trim();

    if (!message) {
      newErrors.message = "Please enter your message.";
    } else if (message.length < 10) {
      newErrors.message =
        "Message must be at least 10 characters.";
    } else if (message.length > 1000) {
      newErrors.message =
        "Message cannot exceed 1000 characters.";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  // ==========================================
  // SUBMIT FORM
  // ==========================================
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Clear previous API message
    setApiMessage({
      type: "",
      message: "",
    });

    // Validate
    const isValid = validateForm();

    if (!isValid) {
      return;
    }

    try {
      setLoading(true);

      // ========================================
      // API REQUEST
      // ========================================
      const response = await fetch("https://sudhir23-001-site1.etempurl.com/api/Ecom/AddInquiry", {
        method: "POST",

        headers: {
          "Content-Type": "application/json",
        },

        body: JSON.stringify({
          name: formData.name.trim(),
          phone: formData.mobile.trim(),
          email: formData.email.trim(),
          services: formData.service,
          message: formData.message.trim(),
        }),
      });

      // Try to read JSON response
      let data = {};

      try {
        data = await response.json();
      } catch {
        data = {};
      }

      // ========================================
      // API ERROR
      // ========================================
      if (!response.ok) {
        throw new Error(
          data?.message ||
            "Unable to submit your request. Please try again."
        );
      }

      // ========================================
      // SUCCESS
      // ========================================
      setApiMessage({
        type: "success",
        message:
          data?.message ||
          "Thank you! Your enquiry has been submitted successfully.",
      });

      // Reset form
      setFormData({
        name: "",
        mobile: "",
        email: "",
        service: "",
        message: "",
      });

      setErrors({});
    } catch (error) {
      console.error("Contact API Error:", error);

      setApiMessage({
        type: "error",
        message:
          error.message ||
          "Something went wrong. Please try again later.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page">

      {/* ==========================================
          PAGE TITLE
      =========================================== */}
      <h1>Contact Us</h1>

      {/* ==========================================
          CONTACT FORM
      =========================================== */}
      <form
        className="contact-form"
        onSubmit={handleSubmit}
        noValidate
      >

        {/* ========================================
            NAME
        ========================================= */}
        <div className="form-group">

          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Your Name"
            autoComplete="name"
            disabled={loading}
          />

          {errors.name && (
            <span className="form-error">
              {errors.name}
            </span>
          )}

        </div>

        {/* ========================================
            MOBILE
        ========================================= */}
        <div className="form-group">

          <input
            type="tel"
            name="mobile"
            value={formData.mobile}
            onChange={handleChange}
            placeholder="Mobile"
            maxLength="10"
            autoComplete="tel"
            disabled={loading}
          />

          {errors.mobile && (
            <span className="form-error">
              {errors.mobile}
            </span>
          )}

        </div>

        {/* ========================================
            EMAIL
        ========================================= */}
        <div className="form-group">

          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="Email"
            autoComplete="email"
            disabled={loading}
          />

          {errors.email && (
            <span className="form-error">
              {errors.email}
            </span>
          )}

        </div>

        {/* ========================================
            SERVICE DROPDOWN
        ========================================= */}
        <div className="form-group">

          <select
            name="service"
            value={formData.service}
            onChange={handleChange}
            disabled={loading}
          >
            <option value="" disabled>
              Select Service
            </option>

            <option value="Ecommerce">
              Ecommerce
            </option>

            <option value="Ecommerce CRM">
              Ecommerce CRM
            </option>

            <option value="Order Management System">
              Order Management System
            </option>

            {/* <option value="SMS Marketing">
              SMS Marketing
            </option>

            <option value="Email Marketing">
              Email Marketing
            </option>

            <option value="WhatsApp Business API">
              WhatsApp Business API
            </option>

            <option value="Business Automation">
              Business Automation
            </option>

            <option value="Other">
              Other
            </option> */}
          </select>

          {errors.service && (
            <span className="form-error">
              {errors.service}
            </span>
          )}

        </div>

        {/* ========================================
            MESSAGE
        ========================================= */}
        <div className="form-group">

          <textarea
            name="message"
            value={formData.message}
            onChange={handleChange}
            rows="4"
            maxLength="1000"
            placeholder="Message"
            disabled={loading}
          />

          <div className="message-footer">

            {errors.message ? (
              <span className="form-error">
                {errors.message}
              </span>
            ) : (
              <span></span>
            )}

            <span className="character-count">
              {formData.message.length}/1000
            </span>

          </div>

        </div>

        {/* ========================================
            API MESSAGE
        ========================================= */}
        {apiMessage.message && (
          <div
            className={
              apiMessage.type === "success"
                ? "api-message success"
                : "api-message error"
            }
          >
            {apiMessage.message}
          </div>
        )}

        {/* ========================================
            SUBMIT BUTTON
        ========================================= */}
        <button
          type="submit"
          disabled={loading}
        >
          {loading ? "Submitting..." : "Submit"}
        </button>

      </form>
    </div>
  );
};

export default Contact;
